import React from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Lock } from '../icons';
import { Panel, vaultWash } from '../common/VaultLayout';
import { colors, fonts } from '../../theme/tokens';

/**
 * Clubhouse-styled login/register gate for auth-required surfaces.
 * AUTH-27 / AUTH-49: preserves return URL via state.from and ?returnTo=.
 */
const AuthRequiredGate = ({
  eyebrow = 'Members Only',
  title = 'Sign In to View',
  body = 'Collector profiles are for signed-in members. Sign in or create an account and we will bring you right back.',
  loading = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}${location.hash}`;

  const goAuth = (path) => {
    const qs = new URLSearchParams();
    if (returnTo && returnTo !== '/login' && returnTo !== '/register') {
      qs.set('returnTo', returnTo);
    }
    const search = qs.toString();
    navigate(search ? `${path}?${search}` : path, {
      state: { from: returnTo },
    });
  };

  return (
    <Box
      sx={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.void,
        backgroundImage: vaultWash,
        px: { xs: 2, sm: 3 },
        py: { xs: 4, sm: 6 },
      }}
    >
      <Panel
        wash
        padding={{ xs: 3, sm: 4 }}
        sx={{
          width: '100%',
          maxWidth: 520,
          textAlign: 'center',
        }}
      >
        {loading ? (
          <Box sx={{ py: 4 }} aria-busy="true" aria-live="polite">
            <CircularProgress sx={{ color: colors.brass }} size={36} />
            <Typography
              sx={{
                mt: 2.5,
                fontFamily: fonts.numeric,
                fontWeight: 600,
                fontSize: '0.875rem',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: colors.textSecondary,
              }}
            >
              Checking session
            </Typography>
          </Box>
        ) : (
          <>
            <Typography
              sx={{
                fontFamily: fonts.numeric,
                fontWeight: 600,
                fontSize: '0.8125rem',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: colors.brass,
                mb: 1.5,
              }}
            >
              {eyebrow}
            </Typography>
            <Lock sx={{ fontSize: '3.25rem', color: colors.brass, mb: 1.5 }} aria-hidden />
            <Typography
              component="h1"
              sx={{
                fontFamily: fonts.impact,
                fontWeight: 400,
                fontSize: { xs: '2.25rem', sm: '2.75rem' },
                textTransform: 'uppercase',
                letterSpacing: '0.01em',
                lineHeight: 0.95,
                color: colors.textPrimary,
                mb: 2,
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontFamily: fonts.body,
                fontSize: '1.0625rem',
                lineHeight: 1.55,
                color: colors.textSecondary,
                maxWidth: 400,
                mx: 'auto',
                mb: 3.5,
              }}
            >
              {body}
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent="center"
              alignItems="stretch"
            >
              <Button
                variant="contained"
                onClick={() => goAuth('/login')}
                sx={{
                  minHeight: 48,
                  px: 3,
                  fontFamily: fonts.display,
                  fontWeight: 400,
                  fontSize: '1rem',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  backgroundColor: colors.brass,
                  color: '#0C0D0F',
                  borderRadius: '2px',
                  '&:hover': { backgroundColor: colors.brassBright },
                }}
              >
                Sign In
              </Button>
              <Button
                variant="outlined"
                onClick={() => goAuth('/register')}
                sx={{
                  minHeight: 48,
                  px: 3,
                  fontFamily: fonts.display,
                  fontWeight: 400,
                  fontSize: '1rem',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  borderColor: colors.brass,
                  color: colors.brass,
                  borderRadius: '2px',
                  '&:hover': {
                    borderColor: colors.brassBright,
                    backgroundColor: colors.panelRaised,
                  },
                }}
              >
                Register
              </Button>
            </Stack>
          </>
        )}
      </Panel>
    </Box>
  );
};

export default AuthRequiredGate;
